import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stack, Typography, Box, alpha, useTheme,
  FormControl, InputLabel, Select, MenuItem, TextField,
  Table, TableHead, TableRow, TableCell, TableBody,
  Chip, LinearProgress, Alert, Divider, Paper, Autocomplete,
  Skeleton,
} from '@mui/material';
import {
  School, Sell, HowToReg, CheckCircle, WarningAmber,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';

import feeAssignmentService from '../../services/feeAssignmentService';
import feePlanService from '../../services/feePlanService';
import { sessionsApi } from '../../services/sessions/sessionsApi';
import { classesApi } from '../../services/sessions/classesApi';
import { sectionInstancesApi } from '../../services/sessions/sectionInstancesApi';
import SearchableSelect from '../common/SearchableSelect';
import { buildSessionOptions } from '../../utils/sessionOptions';

import {
  FeePlanRow, BulkAssignPreview,
} from '../../types/fees';
import { Session, SessionClass, SectionInstance } from '../../types/sessions';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';

interface BulkAssignFeesDialogProps {
  open: boolean;
  /** Optional — pre-selects a plan if the dialog was launched from a plan row. */
  initialPlanId?: string | null;
  onClose: () => void;
  onAssigned: (result: { created: number; skipped: number }) => void;
}

/**
 * BulkAssignFeesDialog — the star of the fee-module UX.
 *
 * Flow
 * ----
 *   1. Admin picks a plan (or starts pre-selected).
 *   2. If the plan is school-wide, admin narrows to a class (required).
 *   3. Dialog fetches a preview: how many students are eligible, how many
 *      already have an active assignment, projected billing amount.
 *   4. Confirm → single transactional bulk-assign — one insertMany + one
 *      plan lockedAt stamp. Result summary shown, dialog closes.
 *
 * Performance notes
 * -----------------
 * - The plan picker uses Autocomplete's client-side filter over a small
 *   pre-loaded catalog (session-scoped). Never types into the network.
 * - Preview and commit are the only network calls; both are indexed hot paths.
 */
const BulkAssignFeesDialog: React.FC<BulkAssignFeesDialogProps> = ({
  open, initialPlanId, onClose, onAssigned,
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  // Catalogs
  const [sessions, setSessions] = useState<Session[]>([]);
  const [classes, setClasses] = useState<SessionClass[]>([]);
  const [sections, setSections] = useState<SectionInstance[]>([]);
  const [plans, setPlans] = useState<FeePlanRow[]>([]);
  const [loadingCatalogs, setLoadingCatalogs] = useState(false);

  // Selection
  const [sessionId, setSessionId] = useState('');
  const [planId, setPlanId] = useState<string | null>(null);
  // Multi-select. Empty = "use plan's own scope" (all plan classes for a
  // scoped plan; the picker forces at least one for school-wide plans).
  const [classIds, setClassIds] = useState<string[]>([]);
  const [sectionInstanceId, setSectionInstanceId] = useState('');

  // Preview / commit
  const [preview, setPreview] = useState<BulkAssignPreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState<{ created: number; skipped: number } | null>(null);

  const currencyFmt = useCurrencyFormat();

  // ─── Catalog loads ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingCatalogs(true);
    (async () => {
      try {
        const [ss, cls] = await Promise.all([
          sessionsApi.list({ includeArchived: false }),
          classesApi.list(),
        ]);
        if (cancelled) return;
        setSessions(ss);
        setClasses(cls);
        if (!sessionId) {
          const active = ss.find(s => s.status === 'active') ?? ss[0];
          if (active) setSessionId(active._id);
        }
      } catch {
        if (!cancelled) enqueueSnackbar('Failed to load session / class pickers', { variant: 'error' });
      } finally {
        if (!cancelled) setLoadingCatalogs(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Plans list scoped to the picked session.
  useEffect(() => {
    if (!open || !sessionId) {
      setPlans([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await feePlanService.listFeePlans({ sessionId, limit: 100 });
        if (cancelled) return;
        setPlans(res.plans);
      } catch {
        if (!cancelled) setPlans([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, sessionId]);

  // Sections are only meaningful when the admin has narrowed to exactly one
  // class. Multi-class selections have no single "section" to filter by, so
  // we clear the section list in every other case.
  const soleClassId = classIds.length === 1 ? classIds[0] : '';
  useEffect(() => {
    if (!open || !sessionId || !soleClassId) {
      setSections([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await sectionInstancesApi.list({ sessionId, classId: soleClassId });
        if (!cancelled) setSections(list);
      } catch {
        if (!cancelled) setSections([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, sessionId, soleClassId]);

  // Initial state on open + when initialPlanId changes.
  useEffect(() => {
    if (!open) return;
    setPreview(null);
    setResult(null);
    setPreviewError(null);
    if (initialPlanId) setPlanId(initialPlanId);
    else setPlanId(null);
    setClassIds([]);
    setSectionInstanceId('');
  }, [open, initialPlanId]);

  const selectedPlan = useMemo(() => plans.find(p => p._id === planId) ?? null, [plans, planId]);

  // When a plan is picked and it has its own scope, inherit those.
  useEffect(() => {
    if (!selectedPlan) return;
    if (selectedPlan.sessionId && selectedPlan.sessionId !== sessionId) {
      setSessionId(String(selectedPlan.sessionId));
    }
    // If the plan already covers exactly one class, pre-select it. Multi-class
    // plans leave the picker blank so admin can choose to narrow or not.
    if ((selectedPlan.classIds?.length ?? 0) === 1) {
      setClassIds([String(selectedPlan.classIds[0])]);
    } else if ((selectedPlan.classIds?.length ?? 0) > 1) {
      setClassIds([]);
    } else {
      setClassIds([]);
    }
    if (selectedPlan.sectionInstanceId) {
      setSectionInstanceId(String(selectedPlan.sectionInstanceId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlan?._id]);

  // ─── Preview ─────────────────────────────────────────────────────────────

  const planCoversAnyClass = Boolean(selectedPlan && (selectedPlan.classIds?.length ?? 0) > 0);
  // If plan covers classes, we can preview immediately (no override needed).
  // If plan is school-wide, admin must pick at least one class to narrow.
  const canPreview = Boolean(planId) && (planCoversAnyClass || classIds.length > 0);

  const runPreview = useCallback(async (signal: AbortSignal) => {
    if (!planId) return;
    setLoadingPreview(true);
    setPreviewError(null);
    // Do NOT clear the existing preview — keep it visible while the new
    // one loads. The refresh should feel like a subtle top-bar, not a
    // panel collapse.
    try {
      const res = await feeAssignmentService.previewBulkAssign({
        planId,
        classIds: classIds.length > 0 ? classIds : null,
        sectionInstanceId: sectionInstanceId || null,
      });
      if (signal.aborted) return;
      setPreview(res);
    } catch (e: any) {
      if (signal.aborted) return;
      setPreviewError(e?.message || 'Could not compute preview');
    } finally {
      if (!signal.aborted) setLoadingPreview(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId, classIds.join(','), sectionInstanceId]);

  // Debounce the preview refresh. Rapid class toggles coalesce into one
  // request 250ms after the last change. Also handles the "deselect all"
  // case: when the picker can no longer be previewed, clear both the
  // stale preview object and the error line so the empty-state alert
  // shows correctly.
  useEffect(() => {
    if (!canPreview) {
      setPreview(null);
      setPreviewError(null);
      setLoadingPreview(false);
      return;
    }
    const ctrl = new AbortController();
    const t = window.setTimeout(() => runPreview(ctrl.signal), 250);
    return () => {
      window.clearTimeout(t);
      ctrl.abort();
    };
  }, [canPreview, runPreview]);

  // ─── Commit ─────────────────────────────────────────────────────────────

  const commit = async () => {
    if (!planId || !preview) return;
    if (preview.newAssignmentsCount === 0) {
      enqueueSnackbar('Nothing to assign — everyone already has an active assignment.', { variant: 'info' });
      return;
    }
    setCommitting(true);
    try {
      const res = await feeAssignmentService.bulkAssign({
        planId,
        classIds: classIds.length > 0 ? classIds : null,
        sectionInstanceId: sectionInstanceId || null,
        skipExisting: true,
      });
      setResult({ created: res.created, skipped: res.skipped });
      onAssigned({ created: res.created, skipped: res.skipped });
      enqueueSnackbar(`Assigned to ${res.created} student(s). ${res.skipped ? `${res.skipped} skipped.` : ''}`, { variant: 'success' });
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Failed to assign', { variant: 'error' });
    } finally {
      setCommitting(false);
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onClose={committing ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <HowToReg color="primary" fontSize="small" />
          <Typography variant="h6" fontWeight={700}>Assign fee plan to a cohort</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Pick a plan and a class. The plan is snapshotted onto every enrolled student in one transaction. Students who already have an active assignment are skipped.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {/* Session + plan pickers */}
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6, display: 'block', mb: 1 }}>
            What to assign
          </Typography>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <SearchableSelect
                label="Session"
                value={sessionId}
                onChange={v => { setSessionId(v); setPlanId(null); setPreview(null); }}
                options={buildSessionOptions(sessions, theme)}
                disabled={loadingCatalogs || Boolean(initialPlanId)}
                clearable={false}
                fullWidth
                minWidth={0}
              />
              <Autocomplete
                fullWidth
                size="small"
                options={plans}
                getOptionLabel={p => p.name}
                value={selectedPlan}
                onChange={(_, v) => { setPlanId(v?._id ?? null); setPreview(null); }}
                disabled={Boolean(initialPlanId) || !sessionId}
                isOptionEqualToValue={(a, b) => a._id === b._id}
                renderOption={(props, opt) => {
                  const classNames = (opt.classes || []).map(c => c.name).filter(Boolean);
                  const scopeLabel =
                    classNames.length === 0 ? 'All classes'
                    : classNames.length <= 3 ? classNames.join(', ')
                    : `${classNames.slice(0, 2).join(', ')} + ${classNames.length - 2} more`;
                  return (
                    <li {...props} key={opt._id}>
                      <Box sx={{ width: '100%' }}>
                        <Stack direction="row" alignItems="baseline" spacing={1}>
                          <Typography variant="body2" fontWeight={600}>{opt.name}</Typography>
                          {opt.lockedAt && (
                            <Chip label="In use" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                          )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {scopeLabel}
                          {opt.section?.name ? ` – ${opt.section.name}` : ''} · {currencyFmt(opt.totalAnnualAmount)}
                        </Typography>
                      </Box>
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Fee plan" placeholder="Search a plan…" />
                )}
              />
            </Stack>

            {/* Scope narrowing (multi-select classes).
                - School-wide plan (classIds empty): admin MUST pick at least one class;
                  can pick many.
                - Single-class plan: class is auto-filled with the plan's class and can't
                  be changed.
                - Multi-class plan: admin may narrow to any subset of the plan's classes,
                  or leave empty to bill every class the plan already covers. */}
            {selectedPlan && (() => {
              const planClassSet = new Set((selectedPlan.classIds || []).map(String));
              const isSingleClassPlan = planClassSet.size === 1;
              const allowedClasses = planClassSet.size > 0
                ? classes.filter(c => planClassSet.has(String(c._id)))
                : classes;
              const value = allowedClasses.filter(c => classIds.includes(String(c._id)));
              const classLabel =
                planClassSet.size === 0 ? 'Classes (required)'
                : isSingleClassPlan     ? 'Class'
                :                          'Narrow to specific classes (optional)';
              const classPlaceholder =
                planClassSet.size === 0    ? 'Pick one or more classes…'
                : isSingleClassPlan        ? ''
                : classIds.length === 0    ? 'All plan classes'
                :                             'Add another class…';
              return (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Autocomplete
                    multiple
                    fullWidth
                    size="small"
                    options={allowedClasses}
                    getOptionLabel={c => c.name}
                    isOptionEqualToValue={(a, b) => a._id === b._id}
                    value={value}
                    onChange={(_, next) => {
                      setClassIds(next.map(c => String(c._id)));
                      setSectionInstanceId('');
                    }}
                    disabled={isSingleClassPlan}
                    renderTags={(chips, getTagProps) =>
                      chips.map((opt, i) => (
                        <Chip
                          {...getTagProps({ index: i })}
                          key={opt._id}
                          label={opt.name}
                          size="small"
                          sx={{
                            height: 22, fontSize: '0.72rem', fontWeight: 600,
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            color: theme.palette.primary.dark,
                            '& .MuiChip-deleteIcon': { fontSize: 14, color: 'inherit', opacity: 0.7 },
                            '& .MuiChip-deleteIcon:hover': { opacity: 1 },
                          }}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={classLabel}
                        placeholder={classPlaceholder}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                  <SearchableSelect
                    label="Section (optional)"
                    value={sectionInstanceId}
                    onChange={setSectionInstanceId}
                    options={sections.map(s => ({ value: s._id, label: s.name }))}
                    placeholder={
                      classIds.length !== 1
                        ? 'Only when a single class is picked'
                        : sections.length ? 'All sections' : 'No sections in this class'
                    }
                    disabled={Boolean(selectedPlan.sectionInstanceId) || classIds.length !== 1 || sections.length === 0}
                    fullWidth
                    minWidth={0}
                  />
                </Stack>
              );
            })()}
          </Stack>
        </Paper>

        {/* Initial-load progress — only when there's no preview to show yet.
            Subsequent refreshes render inside the preview panel below as a
            thin overlay bar so the panel stays put. */}
        {loadingPreview && !preview && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Loading eligible students…
            </Typography>
          </Box>
        )}

        {previewError && (
          <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
            {previewError}
          </Alert>
        )}

        {preview && (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 1, position: 'relative', overflow: 'hidden' }}>
            {/* Thin overlay bar during refresh — the panel below never
                shifts, so the modal feels stable when the admin toggles
                classes. */}
            {loadingPreview && (
              <LinearProgress
                sx={{
                  position: 'absolute',
                  top: 0, left: 0, right: 0,
                  height: 2,
                }}
              />
            )}
            <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1 }}>
              <Sell fontSize="small" sx={{ color: theme.palette.info.main }} />
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6 }}>
                Preview
              </Typography>
            </Stack>

            {/* Scope + KPIs */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
              <Chip
                icon={<School sx={{ fontSize: '0.9rem !important' }} />}
                label={(() => {
                  const classNames = preview.scopeNames.classes.map(c => c.name).filter(Boolean);
                  const classPart =
                    classNames.length === 0 ? 'All classes'
                    : classNames.length <= 3 ? classNames.join(', ')
                    : `${classNames.slice(0, 2).join(', ')} + ${classNames.length - 2} more`;
                  return `${preview.scopeNames.session?.name ?? '—'} · ${classPart}${preview.scopeNames.section?.name ? ` – ${preview.scopeNames.section.name}` : ''}`;
                })()}
                variant="outlined"
                size="small"
              />
              <Box sx={{ flex: 1 }} />
              <Chip
                icon={<CheckCircle sx={{ fontSize: '0.9rem !important' }} />}
                label={`${preview.newAssignmentsCount} new assignment${preview.newAssignmentsCount === 1 ? '' : 's'}`}
                size="small"
                sx={{
                  height: 24, fontWeight: 600,
                  bgcolor: alpha(theme.palette.success.main, 0.12),
                  color: theme.palette.success.dark,
                  borderColor: alpha(theme.palette.success.main, 0.3),
                  '& .MuiChip-icon': { color: 'inherit' },
                }}
                variant="outlined"
              />
              {preview.alreadyAssignedCount > 0 && (
                <Chip
                  icon={<WarningAmber sx={{ fontSize: '0.9rem !important' }} />}
                  label={`${preview.alreadyAssignedCount} already assigned`}
                  size="small"
                  sx={{
                    height: 24, fontWeight: 600,
                    bgcolor: alpha(theme.palette.warning.main, 0.14),
                    color: theme.palette.warning.dark,
                    borderColor: alpha(theme.palette.warning.main, 0.34),
                    '& .MuiChip-icon': { color: 'inherit' },
                  }}
                  variant="outlined"
                />
              )}
            </Stack>

            <Divider sx={{ mb: 1.5 }} />

            {/* Totals */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mb: 1.5, alignItems: 'center' }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Plan total per student</Typography>
                <Typography variant="body1" fontWeight={700}>
                  {currencyFmt(preview.plan.totalAnnualAmount)}
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box>
                <Typography variant="caption" color="text.secondary">Projected billing</Typography>
                <Typography variant="body1" fontWeight={700} color="primary.dark">
                  {currencyFmt(preview.totalBilling)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {preview.newAssignmentsCount} × {currencyFmt(preview.plan.totalAnnualAmount)}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }} />
              <Box>
                <Typography variant="caption" color="text.secondary">Effective window</Typography>
                <Typography variant="body2">
                  {preview.scopeNames.session?.startDate ? format(new Date(preview.scopeNames.session.startDate), 'MMM d, yyyy') : '—'}
                  {' → '}
                  {preview.scopeNames.session?.endDate ? format(new Date(preview.scopeNames.session.endDate), 'MMM d, yyyy') : '—'}
                </Typography>
              </Box>
            </Stack>

            {/* Already-assigned peek — shown only if any */}
            {preview.alreadyAssigned.length > 0 && (
              <Box sx={{
                mt: 1, p: 1.25, borderRadius: 1.5,
                bgcolor: alpha(theme.palette.warning.main, 0.05),
                border: `1px dashed ${alpha(theme.palette.warning.main, 0.3)}`,
              }}>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75 }}>
                  Skipping — already assigned
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ pl: 0, py: 0.5 }}>Student</TableCell>
                      <TableCell sx={{ py: 0.5 }}>Roll</TableCell>
                      <TableCell sx={{ py: 0.5, pr: 0 }}>Current plan</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.alreadyAssigned.map(r => (
                      <TableRow key={r.enrollmentId}>
                        <TableCell sx={{ pl: 0, py: 0.5 }}>
                          <Typography variant="body2">{r.studentName}</Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.5 }}>
                          <Typography variant="body2">{r.rollNumber}</Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, pr: 0 }}>
                          <Typography variant="body2" color="text.secondary">
                            {r.existingPlanName ?? '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {preview.alreadyAssignedCount > preview.alreadyAssigned.length && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    …and {preview.alreadyAssignedCount - preview.alreadyAssigned.length} more.
                  </Typography>
                )}
              </Box>
            )}

            {preview.plan.isLocked && (
              <Alert severity="info" variant="outlined" sx={{ mt: 1.5 }}>
                This plan is already in use by other assignments. New assignments will use the same snapshot; the plan itself stays untouched.
              </Alert>
            )}
          </Paper>
        )}

        {!preview && !loadingPreview && !previewError && selectedPlan && !canPreview && (
          <Alert severity="info" variant="outlined">
            This plan is school-wide. Pick one or more classes to see who will get billed.
          </Alert>
        )}

        {!selectedPlan && (
          <Box sx={{
            py: 4, textAlign: 'center',
            border: `1px dashed ${theme.palette.divider}`,
            borderRadius: 2,
          }}>
            <HowToReg sx={{ fontSize: 34, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" fontWeight={500} sx={{ mb: 0.25 }}>Pick a plan to preview the cohort</Typography>
            <Typography variant="caption" color="text.secondary">
              The preview shows how many students will be billed and how many are being skipped.
            </Typography>
          </Box>
        )}

        {committing && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Creating assignments…
            </Typography>
          </Box>
        )}

        {result && (
          <Alert severity="success" variant="outlined" sx={{ mt: 2 }}>
            Assigned <strong>{result.created}</strong> student{result.created === 1 ? '' : 's'}.
            {result.skipped > 0 ? ` Skipped ${result.skipped} who already had a plan.` : ''}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={committing}>
          {result ? 'Close' : 'Cancel'}
        </Button>
        {!result && (
          <Button
            variant="contained"
            disableElevation
            onClick={commit}
            disabled={committing || !preview || preview.newAssignmentsCount === 0}
          >
            {committing
              ? 'Assigning…'
              : preview
                ? `Assign to ${preview.newAssignmentsCount} student${preview.newAssignmentsCount === 1 ? '' : 's'}`
                : 'Assign'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkAssignFeesDialog;
